import React, { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
// components
import CircularProgressBar, {
  IPathColorTypes,
} from '@/components/ui/CircularProgressBar';
import {
  getTimerObject,
  calculateSecondsLeftFromTimer,
} from '@/components/CountdownTimerRound';
import IconTextButton, {
  IIconTextButtonTypes,
} from '@/components/ui/buttons/IconTextButton';
// types
import {
  INotificationTextProps,
  INotificationPositionTypes,
  INotificationContentTypes,
} from './types';
import { IContentColors } from '@/components/ui/ExpandableContent';
import { IUIIcons } from '@/types';
// utils
import { getCssVar } from '@/utils/cssVars';
// styles
import styles from './styles.module.scss';
// icon
import { X } from 'react-feather';

const Notification = (props: INotificationTextProps) => {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const dispatch = useDispatch();
  const {
    position = INotificationPositionTypes.BOTTOM_LEFT,
    isVisible,
    contentType,
    durationTime = 1000,
    redirectLink,
    message,
  } = props;

  const composeClassName = classnames(
    styles.notificationContainer,
    `${styles[position]}`,
  );

  const timerIntervalRef = useRef<Timeout | null>(null);
  const [timer, setTimer] = useState(getTimerObject(durationTime / 1000));
  // warning message state for 3 seconds. this before closing timer starts
  const [warningMessageMode, setWarningMessageMode] = useState(true);
  // notification timer
  const [notificationMode, setNotificationMode] = useState(isVisible);

  const deleteNotification = () => {
    setNotificationMode(false);
  };

  const decrementTimer = () => {
    setTimer(prevTimer =>
      getTimerObject(calculateSecondsLeftFromTimer(prevTimer) - 1),
    );
    return null;
  };

  const startTimer = () => {
    if (timerIntervalRef.current) return;
    timerIntervalRef.current = setInterval(decrementTimer, 1000);
    return;
  };

  useEffect(() => {
    if (isVisible) {
      setNotificationMode(isVisible);
      if (contentType === INotificationContentTypes.ATTENDEE_DELETION) {
        // Three mins waiting time to read warning for attendee.  why we are log out attendee ( before count down starts)
        setInterval(() => {
          setWarningMessageMode(false);
          setInterval(() => {
            setWarningMessageMode(true);
            if (durationTime) {
              deleteNotification();
              // Force logout attendee from live event page
              dispatch({
                type: 'account/logout',
                payload: { eventId }
              });
            }
          }, durationTime);
          startTimer();
        }, 3000);
      }
      else {
        setInterval(() => {
          if (durationTime) {
            deleteNotification();
          }
        }, durationTime);
      }
    }
  }, [isVisible]);

  // attendee deletion counter and notification content
  const getNotificationContent = (() => {
    if (contentType === INotificationContentTypes.ATTENDEE_DELETION) {
      return (
        <>
          <div>
            {!warningMessageMode && (
              <div className={styles.progress}>
                <CircularProgressBar
                  valuePercent={timer.seconds * 10}
                  color={IPathColorTypes.DANGER}
                  strokeWidth={8}
                >
                  <div className={styles.timerCount}>{timer.seconds}</div>
                </CircularProgressBar>
              </div>
            )}
          </div>
          <div>
            <div className={styles.attendeeDeleteHeader}>
              <div className={styles.headerAttendeeDeletion}>
                You are being logged out.
              </div>
              <div onClick={deleteNotification}>
                <X size={13} color={getCssVar(IContentColors.WHITE)} />
              </div>
            </div>
            {warningMessageMode && (
              <div className={styles.attendeeWarningMessage}>
                You have been removed from the event. Ending your current
                session in <b className={styles.boldTime}>10 seconds.</b>
              </div>
            )}
            {!warningMessageMode && (
              <div className={styles.attendeeDeletionMessage}>
                You have been removed from the event. Please contact the
                organiser to resolve the issue.
              </div>
            )}
          </div>
        </>
      );
    } else if (contentType === INotificationContentTypes.CUSTOM_NOTIFICATION) {
      return (<div className={styles.customNotification} dangerouslySetInnerHTML={{ __html: message }}></div>)
    } else if (
      contentType === INotificationContentTypes.ATTENDEE_DELETION_LANDING_PAGE
    ) {
      return (
        <div>
          <div className={styles.attendeeDeleteHeader}>
            <div className={styles.headerAttendeeDeletion}>
              You have been removed from the event
            </div>
            <div onClick={deleteNotification}>
              <X size={13} color={getCssVar(IContentColors.WHITE)} />
            </div>
          </div>
          <div className={styles.attendeeWarningMessage}>
            The organiser has removed you from the event. You can no longer
            login into the event. Please contact the organiser to resolve the
            issue.
          </div>
        </div>
      );
    }
    if (contentType === INotificationContentTypes.LOBBY_REDIRECT) {
      return (
        <div>
          <div className={styles.redirectHeaderSecion}>
            <div className={styles.headerAttendeeRedirection}>
              Join Live Session
            </div>
            <div onClick={deleteNotification}>
              <X size={13} color={getCssVar(IContentColors.WHITE)} />
            </div>
          </div>
          <div className={styles.attendeeRedirectMessage}>
            The event is currently live. Would you like to join the session?
          </div>
          <div className={styles.redirectButton}>
            <Link to={redirectLink}>
              <IconTextButton
                activeIcon={IUIIcons.FORWARD_NAVIGATION}
                defaultIcon={IUIIcons.FORWARD_NAVIGATION}
                defaultLabel="Join Session"
                activeLabel="Join Session"
                showDefault={true}
                buttonType={IIconTextButtonTypes.PRIMARY_SQUARE_FULL}
              />
            </Link>
          </div>
        </div>
      );
    }
    if (contentType === INotificationContentTypes.LOBBY_REDIRECT) {
      return (
        <div>
          <div className={styles.attendeeDeleteHeader}>
            <div className={styles.headerAttendeeRedirection}>
              Join Live Session
            </div>
            <div onClick={deleteNotification}>
              <X size={13} color={getCssVar(IContentColors.WHITE)} />
            </div>
          </div>
          <div className={styles.attendeeRedirectMessage}>
            The event is currently live. Would you like to join the session?
          </div>
          <div className={styles.redirectButton}>
            <Link to={redirectLink}>
              <IconTextButton
                activeIcon={IUIIcons.FORWARD_NAVIGATION}
                defaultIcon={IUIIcons.FORWARD_NAVIGATION}
                defaultLabel="Join Session"
                activeLabel="Join Session"
                showDefault={true}
                buttonType={IIconTextButtonTypes.PRIMARY_SQUARE_FULL}
              />
            </Link>
            The organiser has removed you from the event. You can no longer
            login into the event. Please contact the organiser to resolve the
            issue.
          </div>
        </div>
      );
    }
    return null;
  })();

  if (notificationMode) {
    return <div className={composeClassName}>{getNotificationContent}</div>;
  }

  return null;
};

export default Notification;
